// src/data/ticajeStore.js
import { TicajeOnlineAdapter as OnlineAdapter } from "./adapters/ticajeOnlineAdapter";
import { TicajeOfflineAdapter as OfflineAdapter } from "./adapters/ticajeOfflineAdapter";

// --- utils storage seguros ---
const jget = (k, def = null) => {
  try {
    const s = localStorage.getItem(k);
    return s ? JSON.parse(s) : def;
  } catch {
    return def;
  }
};
const jset = (k, v) => {
  try {
    localStorage.setItem(k, JSON.stringify(v));
  } catch {}
};
const jdel = (k) => {
  try {
    localStorage.removeItem(k);
  } catch {}
};
const uuid = () =>
  crypto?.randomUUID?.() || `k_${Date.now()}_${Math.random().toString(36).slice(2)}`;

const KEYS = {
  ordenes: "pwa.cache.ordenes",                 // lista de OTs activas
  clientes: "pwa.cache.clientes",               // lista de clientes
  centrosPorOrden: "pwa.cache.ordenes.centros", // { [ordenId]: [centros] }
  localActive: "pwa.ticaje.activo",             // intervención activa local / stub híbrida
};

function nowIso() {
  return new Date().toISOString();
}

class TicajeStore {
  constructor() {
    this._online = !!navigator.onLine;
    this._active = null;  // lo que la UI debe ver (server o local)
    this._local  = null;  // intervención nacida offline o stub pendiente (id "local:*")
    this._reconciling = false;
    this._listeners = new Set();
  }

  // -------- eventos / snapshot --------
  subscribe(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  _emit(evt = {}) {
    for (const fn of this._listeners) {
      try { fn(evt); } catch {}
    }
  }

  snapshot() {
    return {
      online: this._online,
      active: this._active,
      local:  this._local,
      reconciling: this._reconciling,
      ordenesCached:   jget(KEYS.ordenes, []),
      clientesCached:  jget(KEYS.clientes, []),
      centrosPorOrden: jget(KEYS.centrosPorOrden, {}),
    };
  }

  // -------- helpers internos --------
  _loadLocalActive() {
    const loc = jget(KEYS.localActive, null);
    this._local = loc || null;
    // preferimos mostrar la local si no está marcada como pending*
    if (this._local && !(this._local.pendingFinish || this._local.pendingCancel)) {
      this._active = this._local;
    }
  }

  _saveLocalActive(obj) {
    this._local  = obj || null;
    this._active = obj || null;
    if (obj) jset(KEYS.localActive, obj);
    else     jdel(KEYS.localActive);
  }

  _clearLocalActive() {
    this._local = null;
    jdel(KEYS.localActive);
    // si la activa apuntaba a la local, la ocultamos; el servidor decidirá
    if (this._active && typeof this._active.id === "string" && this._active.id.startsWith("local:")) {
      this._active = null;
    }
  }

  _getCentrosCache() {
    return jget(KEYS.centrosPorOrden, {});
  }

  _setCentrosCache(map) {
    jset(KEYS.centrosPorOrden, map || {});
  }

  // -------- red --------
  setOnline(v) {
    const was = this._online;
    this._online = !!v;
    this._emit({ type: "net:set", online: this._online });
    if (!was && this._online) {
      // al recuperar red → reconcilia
      this.reconcile().catch(() => {});
    }
  }

  // -------- bootstrap --------
  /**
   * Se llama al entrar en la pantalla de intervenciones.
   * - Precalienta ordenes + clientes cuando hay red.
   * - Recupera activa local/server.
   * - Lanza reconcile si procede.
   */
  async bootstrap() {
    // 1) catálogos básicos
    if (this._online) {
      try {
        const [ordenes, clientes] = await Promise.all([
          OnlineAdapter.getOrdenes?.() || [],
          OnlineAdapter.getClientes?.() || [],
        ]);

        if (Array.isArray(ordenes) && ordenes.length) jset(KEYS.ordenes, ordenes);
        if (Array.isArray(clientes) && clientes.length) jset(KEYS.clientes, clientes);
      } catch {
        // si falla, simplemente nos quedamos con caché previa
      }
    }

    // 2) activa preferente (local vs servidor)
    this._loadLocalActive();
    if (this._online) {
      try {
        const s = await OnlineAdapter.getActive?.(); // null si no hay
        if (s) {
          this._active = s;
          this._clearLocalActive(); // descarta local si la hubiera
        }
      } catch {}
    }
    this._emit({ type: "bootstrap" });

    // 3) al estar online intenta reconciliar la outbox
    if (this._online) {
      await this.reconcile().catch(() => {});
    }
  }

  // -------- lecturas --------
  getActive() { return this._active; }
  getLocal()  { return this._local;  }

  async getOrdenes() {
    const cached = jget(KEYS.ordenes, []);
    if (cached?.length) return cached;
    if (this._online && OnlineAdapter.getOrdenes) {
      const ordenes = await OnlineAdapter.getOrdenes();
      if (Array.isArray(ordenes) && ordenes.length) jset(KEYS.ordenes, ordenes);
      return ordenes;
    }
    return [];
  }

  async getClientes() {
    const cached = jget(KEYS.clientes, []);
    if (cached?.length) return cached;
    if (this._online && OnlineAdapter.getClientes) {
      const clientes = await OnlineAdapter.getClientes();
      if (Array.isArray(clientes) && clientes.length) jset(KEYS.clientes, clientes);
      return clientes;
    }
    return [];
  }

  /**
   * Centros asociados a una OT:
   * - Primero intenta leer de caché { [ordenId]: centros[] }.
   * - Si no hay y estamos online, pide al backend y cachea.
   */
  async getCentrosPorOrden(ordenId) {
    const id = Number(ordenId);
    if (!id) return [];

    const cache = this._getCentrosCache();
    if (Array.isArray(cache[id]) && cache[id].length) {
      return cache[id];
    }

    if (this._online && OnlineAdapter.getCentrosPorOrden) {
      const centros = await OnlineAdapter.getCentrosPorOrden(id);
      const map = this._getCentrosCache();
      map[id] = Array.isArray(centros) ? centros : [];
      this._setCentrosCache(map);
      return map[id];
    }

    return [];
  }

  /**
   * Alias para que el componente use getCentrosByOrden(...)
   * (IniciarIntervencion llama a ticajeStore.getCentrosByOrden)
   */
  async getCentrosByOrden(ordenId) {
    return this.getCentrosPorOrden(ordenId);
  }

  // -------- mutaciones UI --------
  /**
   * Inicia una intervención (ticaje).
   * Espera (como mínimo):
   *  - ordenId
   *  - clienteId
   *  - centroId, centroNombre
   *  - latitud, longitud, fingerprint
   *  - comentarios (opcional)
   *
   * ordenTitulo / clienteNombre se pueden omitir: se resuelven desde la caché cuando sea posible.
   */
  async start({
    ordenId,
    ordenTitulo,     // opcional
    clienteId,
    clienteNombre,   // opcional
    centroId,
    centroNombre,
    latitud,
    longitud,
    fingerprint,
    comentarios = "",
  }) {
    // ⚠️ Si hay activa o local (incluida pendiente), no iniciar otra
    if (this._active || this._local) {
      return { status: "noop" };
    }

    const ts = nowIso();

    // Resolvemos nombres amigables desde la caché si no vienen
    const cachedOrdenes  = jget(KEYS.ordenes, []);
    const cachedClientes = jget(KEYS.clientes, []);

    const ord = cachedOrdenes.find((o) => Number(o.id) === Number(ordenId));
    const cli = cachedClientes.find((c) => Number(c.id) === Number(clienteId));

    const safeOrdenTitulo   = ordenTitulo   || ord?.titulo  || "";
    const safeClienteNombre = clienteNombre || cli?.nombre  || "";
    const safeCentroNombre  = centroNombre  || "";

    const common = {
      cliente_id: Number(clienteId),
      centro_id:  Number(centroId),
      orden_id:   Number(ordenId),
      comentarios: comentarios || null,
      latitud,
      longitud,
      fingerprint,
      inicio: ts,
      inicio_client: ts, // usado en replays offline
    };

    // Intento ONLINE con fallback robusto a OFFLINE
    if (this._online) {
      try {
        const r = await OnlineAdapter.start(common);
        if (r?.ok && !r.conflict) {
          const s = await OnlineAdapter.getActive().catch(() => null);
          if (s) {
            this._active = s;
            this._clearLocalActive();
            this._emit({ type: "start:online", active: this._active });
            return { status: "online", data: s };
          }
        } else if (r?.status === 409 || r?.conflict) {
          // ya hay intervención activa en servidor → recupérala
          const s = await OnlineAdapter.getActive().catch(() => null);
          if (s) {
            this._active = s;
            this._clearLocalActive();
            this._emit({ type: "start:conflict", active: this._active });
            return { status: "online", data: s };
          }
        }
      } catch {
        // red caida/timeout → fallback a offline
      }
    }

    // OFFLINE inmediato: crea activa local y encola (lo hace el OfflineAdapter)
    const local = OfflineAdapter.start({
      ...common,
      orden_titulo:   safeOrdenTitulo,
      cliente_nombre: safeClienteNombre,
      centro_nombre:  safeCentroNombre,
      // debe crear outbox START con groupKey y payload = common
    });

    // esperamos que OfflineAdapter.start devuelva { active }
    const activeLocal = local?.active || {
      id: `local:${uuid()}`,
      inicio: common.inicio,
      cliente_id: Number(clienteId),
      centro_id:  Number(centroId),
      orden_id:   Number(ordenId),
      orden_titulo:   safeOrdenTitulo,
      cliente_nombre: safeClienteNombre,
      centro_nombre:  safeCentroNombre,
      latitud_inicio:  latitud,
      longitud_inicio: longitud,
      pendingFinish: false,
      pendingCancel: false,
    };

    this._saveLocalActive(activeLocal);
    this._emit({ type: "start:offline", active: this._active });
    return { status: "offline", data: { local: activeLocal } };
  }

  /**
   * Finalizar intervención (ticaje) con geolocalización de cierre.
   */
  async finish({ latitud, longitud, enRango, metros }) {
    if (!this._active && !this._local) return { status: "noop" };

    const current = this._active || this._local;
    const isLocal =
      typeof current.id === "string" && current.id.startsWith("local:");

    const basePayload = {
      motivo_fin: "finalizada",
      latitud_cierre: latitud,
      longitud_cierre: longitud,
      en_rango_cierre: !!enRango,
      distancia_m_cierre: Math.round(metros || 0),
    };

    // 🔁 OFFLINE (intervención nacida offline O nacida online pero estamos sin red)
    if (isLocal || !this._online) {
      const offlinePayload = {
        ...basePayload,
        fin_client: nowIso(),
        // contexto opcional para que OfflineAdapter pueda crear stub si no hay localActive
        cliente_id: current.cliente_id ?? null,
        centro_id:  current.centro_id ?? null,
        orden_id:   current.orden_id ?? null,
        orden_titulo:   current.orden_titulo || "",
        cliente_nombre: current.cliente_nombre || "",
        centro_nombre:  current.centro_nombre || "",
        inicio: current.inicio ?? null,
        latitud_inicio:  current.latitud_inicio ?? null,
        longitud_inicio: current.longitud_inicio ?? null,
      };

      const res = OfflineAdapter.finish(offlinePayload) || {};
      if (res.active) {
        this._saveLocalActive(res.active);
      }

      this._emit({ type: "finish:queued", active: this._active });
      return { status: "offline" };
    }

    // 🌐 ONLINE directo (usará PUT /ticajes/finalizar/actual)
    await OnlineAdapter.finishActual(basePayload);
    this._active = null;
    this._emit({ type: "finish:online" });
    return { status: "online" };
  }

  /**
   * Cancelar intervención (sin geoloc de cierre).
   */
  async cancel() {
    if (!this._active && !this._local) return { status: "noop" };

    const current = this._active || this._local;
    const isLocal =
      typeof current.id === "string" && current.id.startsWith("local:");

    if (isLocal || !this._online) {
      const res = OfflineAdapter.cancel() || {};
      if (res.active) {
        this._saveLocalActive(res.active);
      }
      this._emit({ type: "cancel:queued", active: this._active });
      return { status: "offline" };
    }

    await OnlineAdapter.cancelActual();
    this._active = null;
    this._emit({ type: "cancel:online" });
    return { status: "online" };
  }

  // -------- reconciliación --------
  async reconcile() {
    if (!this._online || this._reconciling) return;
    this._reconciling = true;
    this._emit({ type: "reconcile:start" });

    try {
      const list = (OfflineAdapter._outboxList?.() || []).sort((a, b) => a.ts - b.ts);
      if (!list.length) {
        // refresco de estado server
        const s = await OnlineAdapter.getActive().catch(() => null);
        this._active = s || null;
        if (s) this._clearLocalActive();
        this._emit({ type: "reconcile:idle", active: this._active });
        return;
      }

      // agrupa por groupKey (una intervención offline completa o stub híbrido)
      const groups = new Map();
      for (const it of list) {
        const key = it.groupKey || "default";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(it);
      }

      for (const [groupKey, items] of groups) {
        const start  = items.find(x => x.type === "START");
        const finish = items.find(x => x.type === "FINISH");
        const cancel = items.find(x => x.type === "CANCEL");
        const ids    = items.map(x => x.id);

        // C) Solo FINISH/CANCEL (inició online, cerró/canceló offline)
        if (!start && (finish || cancel)) {
          try {
            if (finish) {
              await OnlineAdapter.finishActual(
                finish.payload,
                { offlineReplay: true }
              );
            } else {
              await OnlineAdapter.cancelActual({ offlineReplay: true });
            }
            OfflineAdapter._outboxRemoveIds?.(ids);
            this._clearLocalActive();
          } catch (e) {
            // si falla se reintentará en la siguiente reconcil
            continue;
          }
          continue;
        }

        // A) Solo START (nació offline)
        if (start && !finish && !cancel) {
          try {
            const r = await OnlineAdapter.start(
              start.payload,
              {
                offlineReplay: true,
                idemKey: start.id || groupKey,
              }
            );
            if (r?.ok || r?.status === 409) {
              const s = await OnlineAdapter.getActive().catch(() => null);
              if (s) {
                this._active = s;
                this._clearLocalActive();
              }
              OfflineAdapter._outboxRemoveIds?.(ids);
            } else {
              continue;
            }
          } catch {
            continue; // red caída → reintento posterior
          }
          continue;
        }

        // B) START + FINISH/CANCEL (ciclo completo offline)
        if (start && (finish || cancel)) {
          try {
            // 1) intenta crear la intervención con inicio_client
            await (async () => {
              try {
                await OnlineAdapter.start(
                  start.payload,
                  {
                    offlineReplay: true,
                    idemKey: start.id || groupKey,
                  }
                );
              } catch (err) {
                if (!(err instanceof Error)) throw err;
                throw err;
              }
            })();

            // 2) cerrar/cancelar con fin_client (si hay FINISH)
            if (finish) {
              await OnlineAdapter.finishActual(
                finish.payload,
                { offlineReplay: true }
              );
            } else {
              await OnlineAdapter.cancelActual({ offlineReplay: true });
            }

            OfflineAdapter._outboxRemoveIds?.(ids);
            this._clearLocalActive();
            this._active = null;
          } catch {
            continue; // reintento posterior
          }
        }
      }

      // refresco final desde servidor
      const s = await OnlineAdapter.getActive().catch(() => null);
      this._active = s || null;
      if (s) this._clearLocalActive();
      this._emit({ type: "reconcile:done", active: this._active });
    } finally {
      this._reconciling = false;
      this._emit({ type: "reconcile:end" });
    }
  }
}

export const ticajeStore = new TicajeStore();
